import _ from "lodash";
import React, { useState } from "react";
import classNames from "classnames";
import { Checkbox, Tooltip, Dialog, Tag } from "@toddle-edu/ds-web";
import { connect } from "react-redux";

import classes from "./PracticeList.scss";
import { useI18n } from "Hooks";
import {
  approvedPracticesStyles,
  disabledCheckboxStyles,
} from "./PracticeListStyles";
import ConditionalWrapper from "Utils/ConditionalWrapper";

import { getSettingValue } from "modules/PermissionModule";
import { TickCircleFilled } from "@toddle-edu/ds-icons";
import { Trans } from "react-i18next";
import { getTypeLabel } from "SnP/modules/SnPUtils";

const ApprovedPracticeMessage = props => {
  const {
    itemTypeLabel,
    itemTitle,
    cycleType,
    cycleName,
    messageLocale,
  } = props;

  return (
    <Tooltip
      dsVersion="2.0"
      tooltip={
        <Trans i18nKey={messageLocale}>
          {{ evidenceTypeLabel: itemTypeLabel }}

          <div className={classes.highlightText}>{{ title: itemTitle }}</div>

          {{ cycleType: _.toLower(cycleType) }}

          <div className={classes.highlightText}>{{ cycleName }}</div>
        </Trans>
      }
      overlayInnerStyle={approvedPracticesStyles.tooltipContainerStyle}
      placement={"bottom"}
    >
      <div className={classes.approvedPractice}>
        <TickCircleFilled size={"xxx-small"} variant="success" />
      </div>
    </Tooltip>
  );
};

const DisabledCheckboxWrapper = ({
  itemTypeLabel,
  cycleType,
  cycleName,
  children,
  isApproved,
}) => {
  return (
    <Tooltip
      dsVersion="2.0"
      placement={"left"}
      overlayInnerStyle={disabledCheckboxStyles.tooltipContainerStyle}
      tooltip={
        <Trans
          i18nKey={
            isApproved
              ? "snp:cannot_untag_admin_selected_evidence_msg"
              : "snp:cannot_untag_other_teacher_suggested_evidence_msg"
          }
        >
          <div className={classes.highlightText}>Some Text</div>
          {{ evidenceTypeLabel: itemTypeLabel }}
          {{ cycleType: _.toLower(cycleType) }}
          <div className={classes.highlightText}>{{ cycleName }}</div>
        </Trans>
      }
    >
      {children}
    </Tooltip>
  );
};

const PracticeList = props => {
  const {
    practices,
    updateSelectedPractices,
    practiceListsCollection,
    itemDetails,
    cycleDetails,
    organizationId,
    testId,
  } = props;

  const { itemTitle, itemTypeLabel, messageLocale } = itemDetails;

  const {
    approvedPractices,
    disabledPractices,
    selectedPractices,
  } = practiceListsCollection;

  const { cycleName, cycleType } = cycleDetails;

  const { t } = useI18n(["common", "snp"]);
  const [showDialogueBox, setDialogueBoxVisibility] = useState(false);
  const [practiceDetails, setPracticeDetails] = useState({});

  const closeDialogueBox = () => {
    setDialogueBoxVisibility(false);
  };

  const { id, isSelected } = practiceDetails;

  const unSelectPractice = () => {
    updateSelectedPractices({ id, isSelected });
  };

  const onClickUntagPractice = () => {
    unSelectPractice();
    setDialogueBoxVisibility(false);
  };

  const requirementLabelSetting = getSettingValue({
    organizationId,
    name: "AppSettingSnpRequirementLabel",
  });

  const requirementLabel = t(`snp:${requirementLabelSetting}`, {
    defaultValue: requirementLabelSetting,
  });

  return (
    <ul className={classes.practiceListContainer}>
      {_.map(practices, ({ label, code, type = "PRACTICE", id }) => {
        const isSelected = _.includes(selectedPractices, id);
        const isDisabled = _.includes(disabledPractices, id);

        const isChecked = isSelected || isDisabled;

        const isApproved = _.includes(approvedPractices, id) && isChecked;

        const practiceClasses = classNames(classes.practice, {
          [classes.disabledPractice]: isDisabled,
        });

        const handlePracticeClick = () => {
          if (isDisabled) return;

          if (isApproved) {
            setPracticeDetails({ id, isSelected: !isChecked });
            setDialogueBoxVisibility(true);
            return;
          }

          updateSelectedPractices({ id, isSelected: !isChecked });
        };

        const practiceDetailsClasses = classNames(classes.practiceDetails, {
          "text-label": type === "PRACTICE",
          "text-body": type !== "PRACTICE",
        });

        const typeLabel = getTypeLabel({
          t,
          requirementLabel,
          type,
        });

        return (
          <li
            className={practiceClasses}
            key={id}
            data-test-id={`${testId}-practiceList-item-${id}`}
            onClick={handlePracticeClick}
          >
            <div className={practiceDetailsClasses}>
              <div className={classes.practiceCode}>
                {`${code}`}
                <span className={classes.type}>
                  <Tag dsVersion="2.0" size="small">
                    {typeLabel}
                  </Tag>
                </span>
                {isApproved && (
                  <ApprovedPracticeMessage
                    itemTypeLabel={itemTypeLabel}
                    cycleName={cycleName}
                    cycleType={cycleType}
                    itemTitle={itemTitle}
                    messageLocale={messageLocale}
                  />
                )}
              </div>
              <div
                dir="auto"
                className={classes.practiceLabel}
              >{`${label}`}</div>
            </div>
            <ConditionalWrapper
              condition={isDisabled}
              wrapper={
                <DisabledCheckboxWrapper
                  itemTypeLabel={itemTypeLabel}
                  cycleType={cycleType}
                  cycleName={cycleName}
                  isApproved={isApproved}
                />
              }
              type={"wrapperAsElement"}
            >
              <div className={classes.checkbox}>
                <Checkbox
                  isChecked={isChecked}
                  _legacy_size={"xx-small"}
                  size={"medium"}
                  disabled={isDisabled}
                  aria-label={`${typeLabel}, ${label}`}
                />
              </div>
            </ConditionalWrapper>
          </li>
        );
      })}
      {showDialogueBox && (
        <Dialog
          dsVersion="2.0"
          variant="delete-dialog"
          title={t("snp:untag_practice")}
          onClose={closeDialogueBox}
          message={t("snp:untag_evidence_disclaimer", {
            evidenceTypeLabel: itemTypeLabel,
            cycleType: _.toLower(cycleType),
            cycleName,
          })}
          buttonOneText={t("common:cancel")}
          onButtonOneClick={closeDialogueBox}
          buttonTwoText={t("snp:untag_practice")}
          onButtonTwoClick={onClickUntagPractice}
        />
      )}
    </ul>
  );
};

const mapStateToProps = state => {
  const organizationId = _.get(state, "login.userInfo.org_id", "");
  return { organizationId };
};

export default connect(mapStateToProps)(PracticeList);
